import { useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { gosafeApi } from "../../api";
import {
  onLoadingPatients,
  onLoading,
  onLoaded,
  onLoadPatients,
  onFailedPatients,
  onErrorMessage,
  onSuccessMessage,
  changePageSize,
  changePage,
  onUpdatePatient,
  changeSort,
  changeFilters,
} from "../../store/secretary/patients";
import { translations, replaceEmptyToNull } from "../../helpers";

export const usePatientsStore = () => {
  const dispatch = useDispatch();

  const {
    isLoadingData,
    isLoading,
    patients,
    message,
    pageSize,
    page,
    totalPages,
    filters,
    sort,
  } = useSelector((state) => state.patients);

  const startGetPatients = useCallback(
    async (page, pageSize, { filters, sort }) => {
      try {
        dispatch(onLoadingPatients());

        const filteredParams = Object.fromEntries(
          Object.entries({
            ...filters,
          }).filter(([_, value]) => value !== "")
        );

        // console.log("filteredParams", filteredParams);
        // console.log("sort", sort);
        const { data } = await gosafeApi.get("patient", {
          params: {
            ...filteredParams,
            orderBy: sort.key,
            orderDirection: sort.direction,
            page,
            pageSize,
          },
        });
        dispatch(onLoadPatients(data));
      } catch (error) {
        dispatch(
          onFailedPatients(
            translations[error.response.data.message] ||
              error.response.data.message
          )
        );
      }
    },
    [dispatch]
  );

  const startChangePageSize = useCallback(
    async (pageSize) => {
      dispatch(changePageSize(pageSize));
      await startGetPatients(page, pageSize, { filters, sort });
    },
    [dispatch, startGetPatients, page, filters, sort]
  );

  const startChangePage = useCallback(
    async (page) => {
      dispatch(changePage(page));
      await startGetPatients(page, pageSize, { filters, sort });
    },
    [dispatch, startGetPatients, pageSize, filters, sort]
  );

  const startAddPage = useCallback(async () => {
    if (totalPages > parseInt(page)) {
      const newPage = (parseInt(page) + 1).toString();
      dispatch(changePage(newPage));
      await startGetPatients(newPage, pageSize, { filters, sort });
    }
  }, [dispatch, startGetPatients, page, pageSize, totalPages, filters, sort]);

  const startRemovePage = useCallback(async () => {
    if (parseInt(page) > 1) {
      const newPage = (parseInt(page) - 1).toString();
      dispatch(changePage(newPage));
      await startGetPatients(newPage, pageSize, { filters, sort });
    }
  }, [dispatch, startGetPatients, page, pageSize, totalPages, filters, sort]);

  const reloadPatients = useCallback(async () => {
    await startGetPatients(page, pageSize, { filters, sort });
  }, [startGetPatients, page, pageSize, filters, sort]);

  const startUpdatePatient = useCallback(
    async (patient) => {
      try {
        dispatch(onLoading());

        // console.log("patient", patient);

        const birthDate = patient.birthDate
          ? `${patient.birthDate}T05:00:00.000Z`
          : null;

        const { id, ...newPatient } = {
          ...replaceEmptyToNull(patient),
          birthDate,
        };

        // console.log("id", id);
        // console.log("newPatient", newPatient);

        const { data } = await gosafeApi.put(`patient/${id}`, newPatient);

        dispatch(onUpdatePatient(data.patient));
        dispatch(onSuccessMessage(translations[data.message] || data.message));
      } catch (error) {
        dispatch(
          onErrorMessage(
            translations[error.response.data.message] ||
              error.response.data.message
          )
        );
      }
    },
    [dispatch]
  );

  const startGetProcedurechildren = useCallback(
    async (procedureParentId) => {
      try {
        dispatch(onLoading());
        const { data } = await gosafeApi.get(
          `procedure/byProcedureParentId/${procedureParentId}`
        );
        dispatch(onLoaded());
        return data;
      } catch (error) {
        dispatch(onLoaded());
        return error.response.data.message;
      }
    },
    [dispatch]
  );

  const startSendQuiz = useCallback(
    async (body, patientId, patientEmail) => {
      try {
        dispatch(onLoading());

        const { procedureChildrenId, ...newBody } = {
          ...replaceEmptyToNull(body),
          patientId,
          procedureId: body.procedureChildrenId
            ? body.procedureChildrenId
            : body.procedureId,
        };

        // console.log("newBody", newBody);

        // const data = {
        //   message: "En la nabue",
        // };

        const { data } = await gosafeApi.post(`startFlow`, newBody);

        dispatch(
          onSuccessMessage(
            `${translations[data.message] || data.message} a ${patientEmail}`
          )
        );
      } catch (error) {
        dispatch(
          onErrorMessage(
            translations[error.response.data.message] ||
              error.response.data.message
          )
        );
      }
    },
    [dispatch]
  );

  const startRegisterPatient = useCallback(
    async (body) => {
      try {
        dispatch(onLoading());

        const birthDate = body.birthDate
          ? `${body.birthDate}T05:00:00.000Z`
          : null;

        const newBody = {
          ...replaceEmptyToNull(body),
          birthDate,
        };

        // console.log("newBody", newBody);

        const { data } = await gosafeApi.post("patient", newBody);

        dispatch(onSuccessMessage(translations[data.message] || data.message));
        return;
      } catch (error) {
        dispatch(
          onErrorMessage(
            translations[error.response.data.message] ||
              error.response.data.message
          )
        );
      }
    },
    [dispatch]
  );

  const startChangeSort = useCallback(
    async (key) => {
      let direction = "asc";

      if (sort.key === key && sort.direction === "asc") {
        direction = "desc";
      }

      const newSort = {
        key,
        direction,
      };

      dispatch(changeSort(newSort));

      await startGetPatients(page, pageSize, { filters, sort: newSort });
    },
    [dispatch, sort, page, pageSize, filters]
  );

  const startSearch = useCallback(
    async (filters) => {
      dispatch(changeFilters(filters));

      await startGetPatients(page, pageSize, { filters, sort });
    },
    [dispatch, page, pageSize, sort]
  );

  const startGetPatientByEncryptedId = useCallback(
    async (encryptedId) => {
      try {
        dispatch(onLoadingPatients());
        const { data } = await gosafeApi.get(
          `patient/encrypted/${encryptedId}`
        );
        dispatch(
          onLoadPatients({ patients: [data.patient], page: 1, totalPages: 1 })
        );
        return true;
      } catch (error) {
        return false;
      }
    },
    [dispatch]
  );

  const startUploadCsv = useCallback(
    async (file, validationError = null) => {
      try {
        dispatch(onLoading());

        if (validationError) {
          dispatch(onErrorMessage(validationError));
          return { success: false };
        }

        const formData = new FormData();
        formData.append("file", file);

        const { data } = await gosafeApi.post("patient/bulk-upload", formData);

        dispatch(onSuccessMessage(translations[data.message] || data.message));
        return { success: true, data };
      } catch (error) {
        const errorMessage =
          translations[error?.response?.data?.message] ||
          error?.response?.data?.message ||
          error?.message;

        dispatch(onErrorMessage(errorMessage));
        return { success: false };
      }
    },
    [dispatch]
  );

  return {
    // Props
    isLoadingData,
    isLoading,
    patients,
    message,
    pageSize,
    page,
    totalPages,
    filters,
    sort,

    // Methods
    startGetPatients,
    startChangePageSize,
    startChangePage,
    startAddPage,
    startRemovePage,
    reloadPatients,
    startUpdatePatient,
    startGetProcedurechildren,
    startSendQuiz,
    startRegisterPatient,
    startChangeSort,
    startSearch,
    startGetPatientByEncryptedId,
    startUploadCsv,
  };
};
